/**
 * What the Desktop's menus offer — the context menu (right-click, Shift+F10,
 * long press) and, in the Mac style, the menu bar. Built from one set of
 * actions so the two never disagree, and from the shared file actions so
 * neither offers more than Browse does for the same file.
 *
 * Wording follows the style: Windows says "Open in new window", "Details",
 * "Tidy up"; a Mac says "Open in New Window", "Get Info", "Clean Up".
 */
import type { VaultDocumentDto, VaultDocumentFolderDto } from '@/lib/api';
import { vaultAreaAllows } from '@/lib/vault-areas';
import {
  ROOT_CONTAINER,
  SORT_KEYS,
  TRASH_KEY,
  type Cell,
  type FolderView,
  type SortKey,
} from '../../lib/desktop-layout';
import {
  ICON_MODES,
  SORT_LABELS,
  STYLE_MODES,
  firstDirection,
  menuModeFor,
  type DesktopStyle,
} from '../../lib/folder-view';
import { openerFor, type FileApp, type FileOpener } from '../../lib/file-openers';
import type { FileActionsController } from '../../hooks/useFileActions';
import { areaOfContainer, type DesktopItem } from './desktop-model';
import type { MenuEntry } from './DesktopContextMenu';
import type { BarMenu, BarMenuEntry } from './MacChrome';

export interface MenuActions {
  style: DesktopStyle;
  canWrite: boolean;
  foldersById: ReadonlyMap<string, VaultDocumentFolderDto>;
  fileActions: FileActionsController;
  hasClipboard: boolean;
  trashFull: boolean;
  /** Everything loaded in a canvas (a window id, or the desktop's container),
   *  and what's selected there. */
  itemsIn: (canvas: string) => readonly DesktopItem[];
  selectedIn: (canvas: string) => readonly DesktopItem[];
  /** Icons sit where they're put in this canvas (the desktop). */
  isFree: (canvas: string) => boolean;

  open: (
    item: DesktopItem,
    container: string,
    options?: { newWindow?: boolean; windowId?: string },
  ) => void;
  preview: (doc: VaultDocumentDto) => void;
  openInApp: (doc: VaultDocumentDto, app: FileApp) => void;
  /** "Open in…" choices for a file, bound to the current plugin file-intake
   *  list (ADR-030). Absent → internal arches only. */
  openerFor?: (doc: VaultDocumentDto) => FileOpener;
  rename: (container: string, key: string) => void;
  cut: (container: string, items: readonly DesktopItem[]) => void;
  paste: (container: string) => void;
  trash: (container: string, items: readonly DesktopItem[]) => void;
  newFolder: (container: string, cell: Cell | null) => void;
  upload: (container: string, cell: Cell | null) => void;
  selectAll: (canvas: string) => void;
  tidy: (container: string) => void;
  resetArrangement: () => void;
  viewOf: (container: string) => FolderView;
  setView: (container: string, view: FolderView) => void;
  desktopView: FolderView;
  setDesktopView: (view: FolderView) => void;
  /** Re-arrange the desktop once by a sort (Windows' "Sort by" on the desktop). */
  sortDesktop: (sort: SortKey) => void;
  emptyTrash: () => void;
  openSettings: () => void;
}

const mod = (style: DesktopStyle) => (style === 'mac' ? '⌘' : 'Ctrl+');

/** The desktop's icon sizes, as each style names them (largest first). */
const DESKTOP_ICON_SIZES: Record<
  DesktopStyle,
  Array<{ mode: (typeof ICON_MODES)[number]; label: string }>
> = {
  windows: [
    { mode: 'icons-lg', label: 'Large icons' },
    { mode: 'icons-md', label: 'Medium icons' },
    { mode: 'icons-sm', label: 'Small icons' },
  ],
  mac: [
    { mode: 'icons-xl', label: 'Extra Large' },
    { mode: 'icons-lg', label: 'Large' },
    { mode: 'icons-md', label: 'Medium' },
    { mode: 'icons-sm', label: 'Small' },
  ],
};

function desktopIconEntries(a: MenuActions): MenuEntry[] {
  const view = a.desktopView;
  return DESKTOP_ICON_SIZES[a.style].map(({ mode, label }) => ({
    label,
    checked: view.mode === mode,
    onSelect: () => a.setDesktopView({ ...view, mode }),
  }));
}

function viewEntries(a: MenuActions, container: string): MenuEntry[] {
  const view = a.viewOf(container);
  const current = menuModeFor(view.mode, a.style);
  const modes =
    container === TRASH_KEY
      ? STYLE_MODES[a.style].filter((m) => m.mode !== 'columns' && m.mode !== 'gallery')
      : STYLE_MODES[a.style];
  return modes.map((option) => ({
    label: option.label,
    checked: current === option.mode,
    onSelect: () => a.setView(container, { ...view, mode: option.mode }),
  }));
}

function sortEntries(
  a: MenuActions,
  view: FolderView,
  onChange: (view: FolderView) => void,
): MenuEntry[] {
  return [
    ...SORT_KEYS.map((key) => ({
      label: a.style === 'mac' && key === 'modified' ? 'Date Modified' : SORT_LABELS[key],
      checked: view.sort === key,
      onSelect: () =>
        onChange({ ...view, sort: key, dir: view.sort === key ? view.dir : firstDirection(key) }),
    })),
    {
      label: 'Ascending',
      separatorBefore: true,
      checked: view.dir === 'asc',
      onSelect: () => onChange({ ...view, dir: 'asc' }),
    },
    {
      label: 'Descending',
      checked: view.dir === 'desc',
      onSelect: () => onChange({ ...view, dir: 'desc' }),
    },
  ];
}

/** The entries for a file (one), in the style's order and wording. */
function fileEntries(
  a: MenuActions,
  container: string,
  item: Extract<DesktopItem, { kind: 'file' }>,
): MenuEntry[] {
  const mac = a.style === 'mac';
  const actions = a.fileActions.actionsFor(item.doc);
  const opener = (a.openerFor ?? openerFor)(item.doc);
  const entries: MenuEntry[] = [{ label: 'Open', onSelect: () => a.open(item, container) }];
  if (opener.apps.length > 0) {
    if (mac) {
      entries.push({
        label: 'Open With',
        items: opener.apps.map((app) => ({
          label: app.label,
          onSelect: () => a.openInApp(item.doc, app),
        })),
      });
    } else {
      for (const app of opener.apps) {
        entries.push({ label: `Open in ${app.label}`, onSelect: () => a.openInApp(item.doc, app) });
      }
    }
  }
  if (opener.preferApp && opener.preview) {
    entries.push({ label: mac ? 'Quick Look' : 'Preview', onSelect: () => a.preview(item.doc) });
  } else if (mac && opener.preview) {
    entries.push({ label: 'Quick Look', shortcut: 'Space', onSelect: () => a.preview(item.doc) });
  }
  entries.push({ label: 'Download', onSelect: actions.download });
  if (actions.rename) {
    entries.push({
      label: 'Rename',
      shortcut: mac ? '↩' : 'F2',
      separatorBefore: true,
      onSelect: () => a.rename(container, item.key),
    });
  }
  if (actions.move) {
    entries.push({ label: mac ? 'Move To…' : 'Move to…', onSelect: actions.move });
    entries.push({
      label: 'Cut',
      shortcut: `${mod(a.style)}X`,
      onSelect: () => a.cut(container, [item]),
    });
  }
  if (actions.giveArcheAccess) {
    entries.push({ label: 'Give an arche access…', onSelect: actions.giveArcheAccess });
  }
  entries.push({
    label: mac ? 'Get Info' : 'Details',
    onSelect: actions.viewDetails,
  });
  if (actions.softDelete) {
    entries.push({
      label: 'Move to Trash',
      shortcut: mac ? '⌘⌫' : 'Del',
      danger: true,
      separatorBefore: true,
      onSelect: actions.softDelete,
    });
  }
  return entries;
}

/** The context menu for a request: an item (or the selection it's in), or a
 *  container's background. */
export function contextMenuEntries(
  a: MenuActions,
  request: { container: string; windowId?: string; item?: DesktopItem; cell?: Cell },
): MenuEntry[] {
  const { container, item, cell, windowId } = request;
  const canvas = windowId ?? container;
  const mac = a.style === 'mac';
  const selected = a.selectedIn(canvas);
  if (item && selected.length > 1 && selected.some((i) => i.key === item.key)) {
    const n = selected.length;
    return [
      {
        label: `Cut ${n} items`,
        shortcut: `${mod(a.style)}X`,
        onSelect: () => a.cut(container, selected),
      },
      {
        label: `Move ${n} items to the Trash`,
        danger: true,
        separatorBefore: true,
        onSelect: () => a.trash(container, selected),
      },
    ];
  }
  if (item?.kind === 'file') return fileEntries(a, container, item);
  if (item?.kind === 'folder') {
    const entries: MenuEntry[] = [
      { label: 'Open', onSelect: () => a.open(item, container, { windowId }) },
      {
        label: mac ? 'Open in New Window' : 'Open in new window',
        onSelect: () => a.open(item, container, { newWindow: true }),
      },
    ];
    const userFolder = a.canWrite && !item.folder.isProtected;
    if (userFolder) {
      entries.push({
        label: 'Rename',
        shortcut: mac ? '↩' : 'F2',
        separatorBefore: true,
        onSelect: () => a.rename(container, item.key),
      });
      entries.push({
        label: 'Cut',
        shortcut: `${mod(a.style)}X`,
        onSelect: () => a.cut(container, [item]),
      });
    }
    if (
      a.hasClipboard &&
      a.canWrite &&
      vaultAreaAllows(areaOfContainer(item.key, a.foldersById), 'upload')
    ) {
      entries.push({
        label: mac ? 'Paste Item' : 'Paste into folder',
        shortcut: `${mod(a.style)}V`,
        onSelect: () => a.paste(item.key),
      });
    }
    if (userFolder) {
      entries.push({
        label: 'Move to Trash',
        shortcut: mac ? '⌘⌫' : 'Del',
        danger: true,
        separatorBefore: true,
        onSelect: () => a.trash(container, [item]),
      });
    }
    return entries;
  }
  if (item?.kind === 'trash') {
    const entries: MenuEntry[] = [{ label: 'Open', onSelect: () => a.open(item, container) }];
    if (a.canWrite && a.trashFull) {
      entries.push({
        label: 'Empty Trash',
        danger: true,
        separatorBefore: true,
        onSelect: a.emptyTrash,
      });
    }
    return entries;
  }

  // A background: the desktop, or a folder window.
  const free = a.isFree(canvas);
  const area = container === TRASH_KEY ? null : areaOfContainer(container, a.foldersById);
  const entries: MenuEntry[] = [];
  if (free) {
    // The desktop: icon size, and a one-off sort.
    entries.push({ label: mac ? 'Icon Size' : 'View', items: desktopIconEntries(a) });
    entries.push({
      label: mac ? 'Sort By' : 'Sort by',
      items: SORT_KEYS.map((key) => ({
        label: mac && key === 'modified' ? 'Date Modified' : SORT_LABELS[key],
        onSelect: () => a.sortDesktop(key),
      })),
    });
  } else {
    entries.push({ label: mac ? 'View' : 'View', items: viewEntries(a, container) });
    const view = a.viewOf(container);
    entries.push({
      label: mac ? 'Sort By' : 'Sort by',
      items: sortEntries(a, view, (next) => a.setView(container, next)),
    });
  }
  if (area && a.canWrite && vaultAreaAllows(area, 'create-folder')) {
    entries.push({
      label: mac ? 'New Folder' : 'New folder',
      separatorBefore: true,
      onSelect: () => a.newFolder(container, cell ?? null),
    });
  }
  if (area && a.canWrite && vaultAreaAllows(area, 'upload')) {
    entries.push({
      label: mac ? 'Upload Files…' : 'Upload files…',
      separatorBefore: !(a.canWrite && vaultAreaAllows(area, 'create-folder')),
      onSelect: () => a.upload(container, cell ?? null),
    });
  }
  if (area && a.hasClipboard && a.canWrite && vaultAreaAllows(area, 'upload')) {
    entries.push({
      label: 'Paste',
      shortcut: `${mod(a.style)}V`,
      onSelect: () => a.paste(container),
    });
  }
  if (a.itemsIn(canvas).length > 0) {
    entries.push({
      label: mac ? 'Select All' : 'Select all',
      shortcut: `${mod(a.style)}A`,
      separatorBefore: true,
      onSelect: () => a.selectAll(canvas),
    });
  }
  if (free) {
    entries.push({ label: mac ? 'Clean Up' : 'Tidy up', onSelect: () => a.tidy(container) });
  }
  if (container === ROOT_CONTAINER && free) {
    entries.push({ label: 'Reset all arrangement', onSelect: a.resetArrangement });
    entries.push({
      label: 'Desktop style…',
      separatorBefore: true,
      onSelect: a.openSettings,
    });
  }
  return entries;
}

/** Map a context entry to a menu-bar entry (same shape, minus danger). */
function toBar(entries: readonly MenuEntry[]): BarMenuEntry[] {
  return entries.map((e) => ({
    label: e.label,
    onSelect: e.onSelect,
    shortcut: e.shortcut,
    checked: e.checked,
    separatorBefore: e.separatorBefore,
    items: e.items ? toBar(e.items) : undefined,
  }));
}

/**
 * The Mac menu bar, for where the person is: the focused window (its folder)
 * or the desktop, and what's selected there.
 */
export function macMenuBar(
  a: MenuActions,
  focus: {
    container: string;
    windowId?: string;
    canBack: boolean;
    canForward: boolean;
    onBack?: () => void;
    onForward?: () => void;
    onUp?: () => void;
    onClose?: () => void;
    onMinimize?: () => void;
    onZoom?: () => void;
    goTo: (container: string) => void;
    windows: Array<{ id: string; title: string; focused: boolean; onSelect: () => void }>;
  },
): BarMenu[] {
  const { container } = focus;
  const canvas = focus.windowId ?? container;
  const selected = a.selectedIn(canvas);
  const single = selected.length === 1 ? selected[0] : null;
  const area = container === TRASH_KEY ? null : areaOfContainer(container, a.foldersById);
  const writableHere = !!area && a.canWrite && vaultAreaAllows(area, 'create-folder');
  const free = a.isFree(canvas);
  const view = free ? a.desktopView : a.viewOf(container);
  const arcSaves = [...a.foldersById.values()].find((f) => f.path === '/Arche Saves');
  const myFias = [...a.foldersById.values()].find((f) => f.path === '/My-Fias');

  return [
    {
      label: 'Fias',
      bold: true,
      entries: [{ label: 'Settings…', shortcut: '⌘,', onSelect: a.openSettings }],
    },
    {
      label: 'File',
      entries: [
        {
          label: 'New Folder',
          disabled: !writableHere,
          onSelect: () => a.newFolder(container, null),
        },
        {
          label: 'Open',
          shortcut: '⌘O',
          disabled: !single,
          onSelect: () => single && a.open(single, container, { windowId: focus.windowId }),
        },
        {
          label: 'Open in New Window',
          disabled: !single || single.kind !== 'folder',
          onSelect: () => single && a.open(single, container, { newWindow: true }),
        },
        {
          label: 'Upload Files…',
          disabled: !area || !a.canWrite || !vaultAreaAllows(area, 'upload'),
          onSelect: () => a.upload(container, null),
        },
        {
          label: 'Get Info',
          separatorBefore: true,
          disabled: !single || single.kind !== 'file',
          onSelect: () =>
            single?.kind === 'file' && a.fileActions.actionsFor(single.doc).viewDetails(),
        },
        {
          label: 'Move to Trash',
          shortcut: '⌘⌫',
          disabled: selected.length === 0 || !a.canWrite,
          onSelect: () => a.trash(container, selected),
        },
        {
          label: 'Close Window',
          separatorBefore: true,
          disabled: !focus.onClose,
          onSelect: focus.onClose,
        },
      ],
    },
    {
      label: 'Edit',
      entries: [
        {
          label: 'Cut',
          shortcut: '⌘X',
          disabled: selected.length === 0,
          onSelect: () => a.cut(container, selected),
        },
        {
          label: 'Paste',
          shortcut: '⌘V',
          disabled: !a.hasClipboard,
          onSelect: () => a.paste(container),
        },
        {
          label: 'Select All',
          shortcut: '⌘A',
          separatorBefore: true,
          onSelect: () => a.selectAll(canvas),
        },
      ],
    },
    {
      label: 'View',
      entries: free
        ? [
            ...toBar(desktopIconEntries(a)),
            {
              label: 'Clean Up',
              separatorBefore: true,
              onSelect: () => a.tidy(container),
            },
            {
              label: 'Sort By',
              items: SORT_KEYS.map((key) => ({
                label: key === 'modified' ? 'Date Modified' : SORT_LABELS[key],
                onSelect: () => a.sortDesktop(key),
              })),
            },
          ]
        : [
            ...toBar(viewEntries(a, container)),
            {
              label: 'Sort By',
              separatorBefore: true,
              items: toBar(sortEntries(a, view, (next) => a.setView(container, next))),
            },
          ],
    },
    {
      label: 'Go',
      entries: [
        { label: 'Back', disabled: !focus.canBack, onSelect: focus.onBack },
        {
          label: 'Forward',
          disabled: !focus.canForward,
          onSelect: focus.onForward,
        },
        {
          label: 'Enclosing Folder',
          shortcut: '⌘↑',
          disabled: !focus.onUp,
          onSelect: focus.onUp,
        },
        { label: 'Documents', separatorBefore: true, onSelect: () => focus.goTo(ROOT_CONTAINER) },
        ...(arcSaves
          ? [{ label: 'Arche Saves', onSelect: () => focus.goTo(arcSaves.folderId) }]
          : []),
        ...(myFias ? [{ label: 'My-Fias', onSelect: () => focus.goTo(myFias.folderId) }] : []),
        { label: 'Trash', onSelect: () => focus.goTo(TRASH_KEY) },
      ],
    },
    {
      label: 'Window',
      entries: [
        {
          label: 'Minimize',
          disabled: !focus.onMinimize,
          onSelect: focus.onMinimize,
        },
        { label: 'Zoom', disabled: !focus.onZoom, onSelect: focus.onZoom },
        ...focus.windows.map((w, i) => ({
          label: w.title,
          checked: w.focused,
          separatorBefore: i === 0,
          onSelect: w.onSelect,
        })),
      ],
    },
  ];
}
